import React, { useState } from 'react';
import clsx from 'clsx';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import type { Solicitacao, SolicitacaoAcesso, AcaoAcesso, StatusAcesso } from '../../../models';
import { useSolicitacaoMutations } from '../../../hooks/data';
import {
  STATUS_ACESSO_LABELS,
  STATUS_ACESSO_BADGE_CLASS,
  CATALOGO_ACESSOS,
  ACAO_ACESSO_SOLICITACAO_LABELS,
} from '../solicitacaoDetailsPage.definitions';

interface AcessosChecklistProps {
  solicitacao: Solicitacao;
  podeEditar: boolean;
}

interface LinhaAcesso {
  sistema: string;
  status: StatusAcesso;
  permiteNaoSeAplica: boolean;
  /** Só no tipo 'acessos' — o catálogo fixo (Admissão/Desligamento) não mostra ação, já
   * que ela é sempre implícita pelo tipo (ver comentário abaixo). */
  acaoLabel?: string;
}

/**
 * Checklist de acessos (Microsoft, GLPI, BeeHome, GitLab, EMIVE, Cartão de Acesso, Chave
 * Casa Raro, etc.) — vive na Solicitação, não na Movimentação (Discovery/Desenho técnico
 * §2). Todos os itens do catálogo aplicáveis (Discovery §19, filtrado por
 * Admissão/Desligamento) já aparecem prontos pra marcar.
 *
 * Um único tipo de controle pra tudo — seletor com as opções lado a lado (Pendente /
 * Concluído / Não se aplica, este só nos itens que admitem) — dá pra ver e escolher direto,
 * sem clicar em ciclo. O cabeçalho mostra o progresso mesmo recolhido. Ação (criar/revogar)
 * é implícita pelo tipo da Solicitação (Admissão = criar, Desligamento = revogar).
 */
export default function AcessosChecklist({ solicitacao, podeEditar }: AcessosChecklistProps) {
  const { atualizarOperacional } = useSolicitacaoMutations();
  // Minimizada por padrão — mesmo raciocínio das Movimentações (MovimentacaoCard):
  // evita poluir a tela logo ao abrir a Solicitação.
  const [expandido, setExpandido] = useState(false);

  const acessos = solicitacao.acessos ?? [];
  const isTipoAcessos = solicitacao.tipoSolicitacao === 'acessos';
  const acao: AcaoAcesso = solicitacao.tipoSolicitacao === 'desligamento' ? 'revogar' : 'criar';

  const catalogoAplicavel = CATALOGO_ACESSOS.filter((item) =>
    item.aplicavelEm.includes(solicitacao.tipoSolicitacao)
  );
  const nomesCatalogo = new Set(catalogoAplicavel.map((item) => item.nome));

  // Tipo 'acessos': só o item único escolhido na abertura (ver NovaSolicitacaoContent) —
  // binário (sem "Não se aplica", não faz sentido pra um pedido pontual) e mostra a ação
  // (Criação/Reset de Senha), já que aqui ela não é implícita como em Admissão/Desligamento.
  // Itens de dados antigos que não batem com o catálogo atual continuam aparecendo (com
  // ciclo de 3 estados, mesmo comportamento de antes), pra não esconder histórico já
  // registrado.
  const linhas: LinhaAcesso[] = isTipoAcessos
    ? acessos.map((a) => ({
        sistema: a.sistema,
        status: a.status,
        permiteNaoSeAplica: false,
        acaoLabel: a.acao === 'resetar_senha' ? ACAO_ACESSO_SOLICITACAO_LABELS.resetar_senha : ACAO_ACESSO_SOLICITACAO_LABELS.criar,
      }))
    : [
        ...catalogoAplicavel.map((item) => ({
          sistema: item.nome,
          status: acessos.find((a) => a.sistema === item.nome)?.status ?? ('pendente' as StatusAcesso),
          permiteNaoSeAplica: item.permiteNaoSeAplica,
        })),
        ...acessos
          .filter((a) => !nomesCatalogo.has(a.sistema))
          .map((a) => ({ sistema: a.sistema, status: a.status, permiteNaoSeAplica: true })),
      ];

  const salvarAcessos = (novosAcessos: SolicitacaoAcesso[]) => {
    atualizarOperacional.mutate({ id: solicitacao.id, acessos: novosAcessos });
  };

  const handleEscolher = (linha: LinhaAcesso, novoStatus: StatusAcesso) => {
    if (novoStatus === linha.status) return;
    const existe = acessos.some((a) => a.sistema === linha.sistema);
    const novosAcessos = existe
      ? acessos.map((a) => (a.sistema === linha.sistema ? { ...a, status: novoStatus } : a))
      : [...acessos, { sistema: linha.sistema, acao, status: novoStatus }];
    salvarAcessos(novosAcessos);
  };

  if (linhas.length === 0) {
    return null;
  }

  const resolvidos = linhas.filter((linha) => linha.status !== 'pendente').length;
  const descricaoAcao = isTipoAcessos ? 'Pedido de acessos' : acao === 'revogar' ? 'Revogar acessos' : 'Criar acessos';

  return (
    <section className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        aria-expanded={expandido}
        className="flex w-full flex-wrap items-center justify-between gap-3 px-5 py-4 text-left"
        onClick={() => setExpandido((atual) => !atual)}
      >
        <span>
          <span className="block font-montserrat text-base font-semibold text-gray-900">Acessos</span>
          <span className="text-sm text-gray-600">{descricaoAcao}</span>
        </span>
        <span className="flex items-center gap-3">
          <span className="text-sm text-gray-700">
            {resolvidos} de {linhas.length} resolvidos
          </span>
          <span className="hidden h-1.5 w-24 overflow-hidden rounded-full bg-gray-200 sm:block" aria-hidden="true">
            <span
              className="block h-full rounded-full bg-primary-500"
              style={{ width: `${Math.round((resolvidos / linhas.length) * 100)}%` }}
            />
          </span>
          {expandido ? (
            <ChevronUpIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
          )}
        </span>
      </button>

      {expandido && (
        // Duas colunas de verdade (metade da lista em cada) pra poder ter a linha entre elas.
        <div className="grid divide-y divide-gray-100 border-t border-gray-100 lg:grid-cols-2 lg:divide-x lg:divide-y-0">
          {[linhas.slice(0, Math.ceil(linhas.length / 2)), linhas.slice(Math.ceil(linhas.length / 2))].map((coluna, indice) => (
        <ul key={indice} className="px-5 py-2">
          {coluna.map((linha) => {
            const opcoes: StatusAcesso[] = linha.permiteNaoSeAplica
              ? ['pendente', 'concluido', 'naoAplicavel']
              : ['pendente', 'concluido'];
            return (
              <li
                key={linha.sistema}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 py-2.5 text-sm last:border-b-0"
              >
                <span className="min-w-0">
                  <span className="font-medium text-gray-900">{linha.sistema}</span>
                  {linha.acaoLabel && <span className="ml-1 text-xs text-gray-500">({linha.acaoLabel})</span>}
                </span>
                {podeEditar ? (
                  <span role="group" aria-label={`Status de ${linha.sistema}`} className="inline-flex rounded-lg border border-gray-200 p-0.5">
                    {opcoes.map((opcao) => (
                      <button
                        key={opcao}
                        type="button"
                        aria-pressed={linha.status === opcao}
                        onClick={() => handleEscolher(linha, opcao)}
                        className={clsx(
                          'rounded-md px-2.5 py-1 text-xs font-semibold transition-colors',
                          linha.status === opcao ? STATUS_ACESSO_BADGE_CLASS[opcao] : 'text-gray-500 hover:bg-gray-50'
                        )}
                      >
                        {STATUS_ACESSO_LABELS[opcao]}
                      </button>
                    ))}
                  </span>
                ) : (
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_ACESSO_BADGE_CLASS[linha.status]}`}>
                    {STATUS_ACESSO_LABELS[linha.status]}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
          ))}
        </div>
      )}
    </section>
  );
}
